import { useState } from 'react';
import type { CSSProperties } from 'react';

/**
 * FEATURE (2026-09-01, Jaime ruling): profile picture must render EVERYWHERE a
 * user appears. One shared circular <Avatar>: shows the photo when `avatarUrl`
 * is present, otherwise deterministic-color initials. Falls back to initials
 * gracefully if the image fails to load.
 */

// Moved from HomeTasks.tsx — same name -> same hue everywhere.
export const avatarColor = (name?: string | null): CSSProperties => {
  const key = String(name || '').trim().toLowerCase();
  let hash = 0;
  for (let i = 0; i < key.length; i++) {
    hash = (hash * 31 + key.charCodeAt(i)) >>> 0;
  }
  const hue = hash % 360;
  return { backgroundColor: `hsl(${hue}, 65%, 45%)` };
};

export const initials = (name?: string | null): string => {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '';
  return parts.slice(0, 2).map((p) => p[0].toUpperCase()).join('');
};

interface AvatarProps {
  name?: string | null;
  avatarUrl?: string | null;
  /** Diameter in px (default 40). */
  size?: number;
  alt?: string;
  className?: string;
}

export default function Avatar({ name, avatarUrl, size = 40, alt, className = '' }: AvatarProps) {
  const [broken, setBroken] = useState(false);
  const showImg = Boolean(avatarUrl) && !broken;
  if (showImg) {
    return (
      <img
        src={avatarUrl as string}
        alt={alt || name || 'Avatar'}
        width={size}
        height={size}
        onError={() => setBroken(true)}
        style={{ width: size, height: size }}
        className={`rounded-full object-cover flex-shrink-0 ${className}`}
      />
    );
  }
  return (
    <span
      aria-hidden={initials(name) === '' || undefined}
      style={{ width: size, height: size, fontSize: Math.max(10, Math.round(size * 0.36)), ...avatarColor(name) }}
      className={`rounded-full text-white font-semibold flex items-center justify-center flex-shrink-0 select-none ${className}`}
    >
      {initials(name)}
    </span>
  );
}
